import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import {
  THEME_PRESETS,
  ThemePreset,
  ThemePresetCategory,
  THEME_PRESET_CATEGORY_ORDER,
  PresetThumbnail,
  CategoryFilterChips,
} from './ThemePresets';
import { ADMIN_COLORS } from '../../theme';

const ALL_FILTER = 'All' as const;
type CategoryFilter = ThemePresetCategory | typeof ALL_FILTER;

export const RAIL_WIDTH = 420;

interface PreviewThemeRailProps {
  open: boolean;
  onToggle: () => void;
  selectedPresetId: string | null;
  onSelect: (preset: ThemePreset) => void;
}

export const PreviewThemeRail: React.FC<
  PreviewThemeRailProps
> = ({ open, onToggle, selectedPresetId, onSelect }) => {
  const [filter, setFilter] = React.useState<CategoryFilter>(ALL_FILTER);
  const visiblePresetCount = THEME_PRESETS.filter(
    (p) => filter === ALL_FILTER || p.category === filter,
  ).length;

  return (
  <Box
    sx={{
      position: 'relative',
      flexShrink: 0,
      width: open ? RAIL_WIDTH : 0,
      transition: 'width 0.25s ease',
      borderLeft: open ? '1px solid' : 'none',
      borderColor: 'divider',
      bgcolor: ADMIN_COLORS.headBg,
    }}
  >
    <Tooltip title={open ? 'Hide themes' : 'Show themes'} placement="left">
      <IconButton
        onClick={onToggle}
        aria-label={open ? 'hide themes' : 'show themes'}
        sx={{
          position: 'absolute',
          top: 24,
          left: -17,
          zIndex: 2,
          width: 34,
          height: 34,
          borderRadius: '50%',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          color: 'text.secondary',
          boxShadow: '0 2px 8px rgba(15,23,42,0.12)',
          transition: 'color 0.15s ease, border-color 0.15s ease',
          '&:hover': {
            bgcolor: 'background.paper',
            color: 'primary.main',
            borderColor: 'primary.main',
          },
        }}
      >
        {open ? (
          <ChevronRightIcon fontSize="small" />
        ) : (
          <ChevronLeftIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>

    {open && (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        {/* Pinned so the filters stay reachable however far the list scrolls. */}
        <Box
          sx={{
            flexShrink: 0,
            px: 2.5,
            pt: 2.5,
            pb: 1.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              mb: 1.5,
            }}
          >
            <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
              Themes
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              {visiblePresetCount}{' '}
              {visiblePresetCount === 1 ? 'style' : 'styles'}
            </Typography>
          </Box>
          <CategoryFilterChips value={filter} onChange={setFilter} />
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2.5, pb: 2.5 }}>
        {THEME_PRESET_CATEGORY_ORDER.filter(
          (category) => filter === ALL_FILTER || filter === category,
        ).map((category) => {
          const presets = THEME_PRESETS.filter(
            (p) => p.category === category,
          );
          if (presets.length === 0) return null;
          return (
            <Box key={category} sx={{ mb: 3, mt: 2.5 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                }}
              >
                {category}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(2, minmax(0, 1fr))',
                  gap: 2,
                  mt: 1.25,
                }}
              >
                {presets.map((preset) => {
                  const selected =
                    preset.id === selectedPresetId;
                  return (
                    <Box
                      key={preset.id}
                      component="button"
                      type="button"
                      onClick={() => onSelect(preset)}
                      aria-pressed={selected}
                      sx={{
                        position: 'relative',
                        p: 0,
                        cursor: 'pointer',
                        textAlign: 'left',
                        overflow: 'hidden',
                        borderRadius: 2.5,
                        bgcolor: 'background.paper',
                        border: '2px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        boxShadow: selected
                          ? (t) => `0 6px 18px ${t.palette.primary.main}26`
                          : '0 1px 2px rgba(15,23,42,0.04)',
                        transition:
                          'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: (t) =>
                            `0 6px 18px ${t.palette.primary.main}26`,
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <PresetThumbnail preset={preset} />
                      {selected && (
                        <CheckCircleIcon
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            fontSize: 20,
                            color: 'primary.main',
                            bgcolor: '#fff',
                            borderRadius: '50%',
                          }}
                        />
                      )}
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          px: 1.5,
                          pt: 1.25,
                          pb: 1.5,
                          borderTop: '1px solid',
                          borderColor: 'divider',
                          color: selected ? 'primary.main' : 'text.primary',
                        }}
                      >
                        {preset.name}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
        </Box>
      </Box>
    )}
  </Box>
  );
};
